import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { updateNotificationInDb } from "@/services/notificationUpdateService";
import { getStatusDisplayName } from "@/utils/statusMappings";

export const useNotificationBulkOperations = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const bulkMarkAsRead = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const promises = notificationIds.map(notificationId =>
        supabase
          .from('notification_read_status')
          .upsert({
            notification_id: notificationId,
            user_id: user.id,
            is_read: true,
            read_at: new Date().toISOString()
          }, {
            onConflict: 'notification_id,user_id'
          })
      );

      const results = await Promise.allSettled(promises);
      const failed = results.filter(r => r.status === 'rejected').length;
      
      return { processed: notificationIds.length, failed };
    },
    onSuccess: ({ processed, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['notification-read-status'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
      
      if (failed === 0) {
        toast.success(`${processed} melding(en) gemarkeerd als gelezen`);
      } else {
        toast.warning(`${processed - failed} van ${processed} melding(en) gemarkeerd als gelezen`);
      }
    },
    onError: (error) => {
      toast.error("Fout bij het markeren als gelezen", {
        description: error.message
      });
    }
  });

  const bulkMarkAsUnread = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const promises = notificationIds.map(notificationId =>
        supabase
          .from('notification_read_status')
          .upsert({
            notification_id: notificationId,
            user_id: user.id,
            is_read: false,
            read_at: null
          }, {
            onConflict: 'notification_id,user_id'
          })
      );

      const results = await Promise.allSettled(promises);
      const failed = results.filter(r => r.status === 'rejected').length;
      
      return { processed: notificationIds.length, failed };
    },
    onSuccess: ({ processed, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['notification-read-status'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
      
      if (failed === 0) {
        toast.success(`${processed} melding(en) gemarkeerd als ongelezen`);
      } else {
        toast.warning(`${processed - failed} van ${processed} melding(en) gemarkeerd als ongelezen`);
      }
    },
    onError: (error) => {
      toast.error("Fout bij het markeren als ongelezen", {
        description: error.message
      });
    }
  });

  const bulkArchive = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const promises = notificationIds.map(id =>
        supabase
          .from('notifications')
          .update({ archived: true })
          .eq('id', id)
      );

      const results = await Promise.allSettled(promises);
      const failed = results.filter(r => r.status === 'rejected').length;
      
      return { processed: notificationIds.length, failed };
    },
    onSuccess: ({ processed, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      if (failed === 0) {
        toast.success(`${processed} melding(en) gearchiveerd`);
      } else {
        toast.warning(`${processed - failed} van ${processed} melding(en) gearchiveerd`);
      }
    },
    onError: (error) => {
      toast.error("Fout bij het archiveren", {
        description: error.message
      });
    }
  });

  const bulkRestore = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const promises = notificationIds.map(id =>
        supabase
          .from('notifications')
          .update({ archived: false })
          .eq('id', id)
      );

      const results = await Promise.allSettled(promises);
      const failed = results.filter(r => r.status === 'rejected').length;
      
      return { processed: notificationIds.length, failed };
    },
    onSuccess: ({ processed, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      if (failed === 0) {
        toast.success(`${processed} melding(en) hersteld`);
      } else {
        toast.warning(`${processed - failed} van ${processed} melding(en) hersteld`);
      }
    },
    onError: (error) => {
      toast.error("Fout bij het herstellen", {
        description: error.message
      });
    }
  });

  const bulkDelete = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const promises = notificationIds.map(id =>
        supabase
          .from('notifications')
          .update({ 
            is_deleted: true, 
            deleted_at: new Date().toISOString() 
          })
          .eq('id', id)
      );

      const results = await Promise.allSettled(promises);
      const failed = results.filter(r => r.status === 'rejected').length;
      
      return { processed: notificationIds.length, failed };
    },
    onSuccess: ({ processed, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      if (failed === 0) {
        toast.success(`${processed} melding(en) verwijderd`);
      } else {
        toast.warning(`${processed - failed} van ${processed} melding(en) verwijderd`);
      }
    },
    onError: (error) => {
      toast.error("Fout bij het verwijderen", {
        description: error.message
      });
    }
  });

  const bulkChangeStatus = useMutation({
    mutationFn: async ({ notificationIds, newStatus }: { notificationIds: string[], newStatus: string }) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      // First fetch all notifications to check their current status
      const { data: notifications, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .in('id', notificationIds);
      
      if (fetchError) throw fetchError;
      
      let updated = 0;
      let skipped = 0;
      let failed = 0;
      
      const updatePromises = (notifications || []).map(async (notification) => {
        // Skip if already has the target status
        if (notification.status === newStatus) {
          skipped++;
          return { status: 'skipped' };
        }
        
        try {
          await updateNotificationInDb(
            notification.id,
            notification,
            { status: newStatus },
            user.id,
            user.name
          );
          updated++;
          return { status: 'fulfilled' };
        } catch (error) {
          failed++;
          return { status: 'rejected', error };
        }
      });
      
      await Promise.all(updatePromises);
      
      return { total: notificationIds.length, updated, skipped, failed };
    },
    onSuccess: ({ total, updated, skipped, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      if (failed === 0 && skipped === 0) {
        toast.success(`${updated} melding(en) status bijgewerkt`);
      } else if (updated > 0) {
        toast.success(`${updated} melding(en) bijgewerkt${skipped > 0 ? `, ${skipped} overgeslagen` : ''}${failed > 0 ? `, ${failed} mislukt` : ''}`);
      } else if (skipped > 0) {
        toast.info(`${skipped} melding(en) hadden al deze status`);
      } else {
        toast.error(`Alle ${failed} melding(en) konden niet worden bijgewerkt`);
      }
    },
    onError: (error) => {
      toast.error("Fout bij het wijzigen van status", {
        description: error.message
      });
    }
  });

  return {
    bulkMarkAsRead,
    bulkMarkAsUnread,
    bulkArchive,
    bulkRestore,
    bulkDelete,
    bulkChangeStatus
  };
};